import { VehiclePoolTab as Tab } from './vehicle-pool-tab.enum';
import type { Car } from '@/entities/cars/car.entity';
import { type MaybeRefOrGetter, ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { Alert } from '@/services/alert.service';
import { useUpdateCar } from '@/queries/use-cars';
import type { TranslationFunction } from '@/i18n';
import { getCurrentCarTyresSeason } from '@/utils/get-current-car-tyres-season.ts';
import { CarTyres } from '@carvia/ros-client-types';
import type { WorkshopAppointment } from '@/entities/workshop-appointments/workshop-appointment.entity';

export enum TabsWithDropdown {
  MERCHANT_CONSULTATION = Tab.MERCHANT_CONSULTATION,
  CAR_INCOMING = Tab.CAR_INCOMING,
  WORKSHOP_APPOINTMENTS = Tab.WORKSHOP_APPOINTMENTS,
  TYRES = Tab.TYRE_CONDITION,
  SELL = Tab.SELL,
}

export const useTodoFunctions = (activeTab: MaybeRefOrGetter<Tab>, t: TranslationFunction) => {
  const router = useRouter();
  const showTodosModal = ref(false);
  const currentTodoCar = ref<Car | undefined>(undefined);

  const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

  const openTodosModal = (car: Car) => {
    showTodosModal.value = true;
    currentTodoCar.value = car;
  };

  const getTodoFunction = (car: Car, workshopAppointment?: WorkshopAppointment) => {
    switch (toValue(activeTab)) {
      case Tab.WORKSHOP_APPOINTMENTS:
        if (workshopAppointment) {
          return () =>
            router.push({
              name: 'editWorkshopAppointment',
              params: { id: workshopAppointment.id },
            });
        }
        return () => openTodosModal(car);
      case Tab.MERCHANT_CONSULTATION:
      case Tab.CAR_INCOMING:
      case Tab.TYRE_CONDITION:
      case Tab.SELL:
        return () => openTodosModal(car);
      case Tab.TYRES_SEASON:
        return async () => {
          const currentTyresSeason = getCurrentCarTyresSeason();
          const { isConfirmed } = await Alert.fire({
            text:
              currentTyresSeason === CarTyres.SUMMER
                ? t('reallyChangedTyresToSummer')
                : t('reallyChangedTyresToWinter'),
            icon: 'question',
            showCancelButton: true,
            cancelButtonText: t('cancel'),
            confirmButtonText:
              currentTyresSeason === CarTyres.SUMMER
                ? t('yesSummerTyresAreApplied')
                : t('yesWinterTyresAreApplied'),
          });
          if (!isConfirmed) return;
          await updateCar({
            id: car.id,
            car: {
              tyres: currentTyresSeason,
            },
          });
        };
    }
    return undefined;
  };

  return {
    getTodoFunction,
    showTodosModal,
    currentTodoCar,
    isUpdatingCar,
  };
};
